import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { getInitials } from "@/lib/get-initials";
import { useAuthStore } from "@/stores/auth-store";
import { Folder, Users, Shield, BarChart3, Lightbulb, Search, Bug, SquareKanban, Wrench, ListChecks } from "lucide-react";
import { HelpButton } from "@/components/common/help-button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HELP_IDS } from "@/lib/help-ids";
import { useChatStore } from "@/stores/chat-store";
import { useMe } from "@/hooks/queries/use-me";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import { TOOLBOX_HOME_PATH } from "@/lib/toolbox/tools";

const navItems = [
  { href: "/dashboard", label: "Dashboard", icon: BarChart3, helpId: HELP_IDS.dock.nav.dashboard },
  { href: "/epic-requests", label: "Requests", icon: Lightbulb, helpId: HELP_IDS.dock.nav.requests },
  { href: "/epics", label: "Epics", icon: Folder, helpId: HELP_IDS.dock.nav.epics },
  { href: "/teams", label: "Teams", icon: Users, helpId: HELP_IDS.dock.nav.teams },
];

const adminNavItems = [
  { href: "/admin/users", label: "Admin", icon: Shield, helpId: HELP_IDS.dock.nav.admin },
];

const fogbugzNavItems = [
  { href: "/fogbugz/tickets", label: "FogBugz", icon: Bug, helpId: HELP_IDS.dock.nav.fogbugz },
];

/** Azure DevOps section; shown only when the user has saved an ADO PAT (PI-SPEC-SW-1362). */
const adoNavItems = [
  { href: "/ado/items", label: "ADO", icon: SquareKanban, helpId: HELP_IDS.dock.nav.ado },
];

/** Grooming (PI-SPEC-SW-1390); needs an ADO PAT and a Toolbox role on some project (`groomingVisible`, E283). */
const groomingNavItems = [
  { href: "/grooming", label: "Grooming", icon: ListChecks, helpId: HELP_IDS.dock.nav.grooming },
];

/**
 * SpecTree Toolbox home; shown only inside the desktop app, where the bridge exists (PI-SPEC-SW-1395).
 * It does not follow `groomingVisible` (E282): the Toolbox also hosts the Formulator, so
 * `groomingVisible` only gates the Grooming entries.
 */
const toolboxNavItems = [
  { href: TOOLBOX_HOME_PATH, label: "Toolbox", icon: Wrench, helpId: HELP_IDS.dock.nav.toolbox },
];

/**
 * Bottom dock navigation bar — Fey.com inspired.
 * Renders a centered pill-shaped dock at the bottom of the viewport.
 */
export function BottomDock() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { dockMode } = useChatStore();
  const { data: meData } = useMe();
  const hasFogbugzKey = meData?.hasFogbugzKey ?? false;
  const hasAdoPat = meData?.hasAdoPat ?? false;
  const groomingVisible = meData?.groomingVisible ?? false;
  const inToolbox = useToolboxBridge() !== null;

  const allItems = [
    ...navItems,
    ...(hasFogbugzKey ? fogbugzNavItems : []),
    ...(hasAdoPat ? adoNavItems : []),
    ...(hasAdoPat && groomingVisible ? groomingNavItems : []),
    ...(inToolbox ? toolboxNavItems : []),
    ...(user?.isGlobalAdmin ? adminNavItems : []),
  ];

  const isSettingsActive = location.pathname.startsWith("/settings");

  return (
    <div>
      <div className={cn(
        "flex items-center gap-1 px-2 py-2 glass-card border border-white/[0.08] shadow-2xl pointer-events-auto",
        dockMode === "bottom"
          ? "rounded-b-2xl rounded-t-none border-t-0"
          : "rounded-2xl"
      )}>
        {allItems.map((item) => {
          const isActive = location.pathname === item.href ||
            location.pathname.startsWith(item.href + "/");
          return (
            <Tooltip key={item.href}>
              <TooltipTrigger asChild>
                <Link
                  to={item.href}
                  data-help-id={item.helpId}
                  className={cn(
                    "flex items-center gap-1.5 h-10 rounded-xl transition-all px-3",
                    isActive
                      ? "bg-white/[0.1] text-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-white/[0.06]"
                  )}
                >
                  <item.icon className="h-[18px] w-[18px] shrink-0" />
                  <span className="text-xs font-medium">{item.label}</span>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                {item.label}
              </TooltipContent>
            </Tooltip>
          );
        })}

        {/* Separator */}
        <div className="w-px h-5 bg-white/[0.08] mx-1" />

        {/* Search trigger (opens command palette) */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => {
                const isMac = navigator.platform.toUpperCase().includes('MAC');
                document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: isMac, ctrlKey: !isMac }));
              }}
              aria-label="Search (⌘K)"
              data-help-id={HELP_IDS.dock.search}
              className="flex items-center justify-center h-10 w-10 rounded-xl text-muted-foreground hover:text-foreground hover:bg-white/[0.06] transition-all"
            >
              <Search className="h-[18px] w-[18px]" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            Search ⌘K
          </TooltipContent>
        </Tooltip>

        {/* Help button */}
        <HelpButton />

        {/* User avatar — links to settings */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => { navigate("/settings"); }}
              aria-label="Go to settings"
              data-help-id={HELP_IDS.dock.avatar}
              className={cn(
                "flex items-center justify-center h-10 w-10 rounded-xl transition-all ml-0.5",
                isSettingsActive
                  ? "ring-2 ring-primary/60 ring-offset-1 ring-offset-background"
                  : "hover:bg-white/[0.06]"
              )}
            >
              <Avatar className="h-6 w-6">
                {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.name} />}
                <AvatarFallback className="text-xs bg-muted">
                  {getInitials(user?.name ?? "")}
                </AvatarFallback>
              </Avatar>
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            Settings
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}
